import { useEffect, useState } from 'react';
import { GitMerge, X, Search } from 'lucide-react';
import { Card, SectionHeader, Badge, Button, Empty } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { DuplicateCandidate, Listing } from '@/lib/types';

type Row = DuplicateCandidate & { a?: Listing; b?: Listing };

export default function Duplicates() {
  const [rows, setRows] = useState<Row[]>([]);
  const [filter, setFilter] = useState<'pending' | 'merged' | 'rejected'>('pending');
  const [threshold, setThreshold] = useState(0.82);

  const load = async () => {
    const { data } = await supabase
      .from('duplicate_candidates')
      .select('*')
      .order('score', { ascending: false })
      .limit(200);
    const dupes = (data as DuplicateCandidate[]) ?? [];
    const ids = Array.from(new Set(dupes.flatMap((d) => [d.listing_id, d.match_listing_id])));
    const { data: lst } = await supabase.from('listings').select('*').in('id', ids);
    const byId = new Map<string, Listing>();
    ((lst as Listing[]) ?? []).forEach((l) => byId.set(l.id, l));
    setRows(dupes.map((d) => ({ ...d, a: byId.get(d.listing_id), b: byId.get(d.match_listing_id) })));

    const { data: t } = await supabase.from('settings').select('*').eq('key', 'duplicate_threshold').maybeSingle();
    if (t && typeof (t as { value: unknown }).value === 'number') setThreshold((t as { value: number }).value);
  };

  useEffect(() => { void load(); }, []);

  const decide = async (id: string, decision: 'merged' | 'rejected') => {
    await supabase.from('duplicate_candidates').update({ status: decision }).eq('id', id);
    setRows((r) => r.map((x) => (x.id === id ? { ...x, status: decision } : x)));
  };

  const saveThreshold = async (v: number) => {
    setThreshold(v);
    await supabase.from('settings').upsert({ key: 'duplicate_threshold', value: v, updated_at: new Date().toISOString() });
  };

  const filtered = rows.filter((r) => r.status === filter);
  const pendingCount = rows.filter((r) => r.status === 'pending').length;
  const autoMergeCount = rows.filter((r) => r.status === 'pending' && r.score >= threshold).length;

  return (
    <div className="space-y-6 text-slate-800">
      <SectionHeader
        title="Duplicate Detection"
        subtitle="Weighted comparison across name, phone, email, website and geolocation. Pairs above threshold merge automatically; the rest queue for admin review."
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-5 md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-bold text-slate-900">Auto-merge threshold</div>
              <div className="text-xs text-slate-500 font-medium">Pairs scoring at or above this value merge without review</div>
            </div>
            <div className="text-2xl font-extrabold text-emerald-600 tabular-nums">{threshold.toFixed(2)}</div>
          </div>
          <input
            type="range"
            min={0.5}
            max={1}
            step={0.01}
            value={threshold}
            onChange={(e) => saveThreshold(parseFloat(e.target.value))}
            className="w-full accent-emerald-600 cursor-pointer"
          />
          <div className="flex justify-between text-[10px] text-slate-400 font-bold mt-1">
            <span>0.50 (loose)</span><span>1.00 (strict)</span>
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Pending pairs</div>
          <div className="text-3xl font-extrabold text-slate-900 mt-1 tabular-nums">{pendingCount}</div>
          <div className="text-xs font-bold text-amber-600 mt-1">Awaiting decision</div>
        </Card>
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Above threshold</div>
          <div className="text-3xl font-extrabold text-slate-900 mt-1 tabular-nums">{autoMergeCount}</div>
          <div className="text-xs font-bold text-emerald-600 mt-1">Would auto-merge on next tick</div>
        </Card>
      </div>

      <div className="flex items-center gap-1 bg-slate-50 rounded-xl p-1 border border-slate-200 w-fit mb-4">
        {(['pending', 'merged', 'rejected'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition cursor-pointer ${
              filter === s ? 'bg-emerald-600 text-white font-bold shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((r) => (
          <Card key={r.id} className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`h-12 w-12 rounded-2xl grid place-items-center font-mono text-sm font-extrabold border ${
                  r.score >= threshold ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}>
                  {Math.round(r.score * 100)}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Match score</div>
                  <div className="text-sm font-bold text-slate-900">{r.score >= threshold ? 'Likely duplicate' : 'Needs review'}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {r.status === 'pending' && (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => decide(r.id, 'rejected')}>
                      <X className="h-3.5 w-3.5" /> Not a duplicate
                    </Button>
                    <Button size="sm" onClick={() => decide(r.id, 'merged')}>
                      <GitMerge className="h-3.5 w-3.5" /> Merge
                    </Button>
                  </>
                )}
                {r.status !== 'pending' && <Badge tone={r.status === 'merged' ? 'success' : 'neutral'}>{r.status}</Badge>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <ListingCard l={r.a} />
              <ListingCard l={r.b} />
            </div>

            <div className="mt-4 grid grid-cols-5 gap-2">
              {(['name', 'phone', 'email', 'website', 'geo'] as const).map((k) => {
                const v = r.signals?.[k] ?? 0;
                return (
                  <div key={k} className="rounded-xl bg-slate-50 border border-slate-200 p-2.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{k}</div>
                    <div className="mt-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                      <div className={`h-full ${v > 0.75 ? 'bg-red-500' : v > 0.5 ? 'bg-amber-500' : 'bg-emerald-600'}`} style={{ width: `${Math.round(v * 100)}%` }} />
                    </div>
                    <div className="text-[11px] font-mono text-slate-600 font-bold mt-1">{(v * 100).toFixed(0)}%</div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <Card className="p-8"><Empty title={`No ${filter} pairs`} hint="Nothing to show for this status." /></Card>
        )}
      </div>
    </div>
  );
}

function ListingCard({ l }: { l?: Listing }) {
  if (!l) return <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-400 font-semibold">Listing removed</div>;
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Search className="h-3.5 w-3.5 text-slate-400" />
        <span className="text-xs font-bold text-slate-900 truncate">{l.name}</span>
      </div>
      <div className="text-[11px] text-slate-600 font-medium space-y-0.5">
        <div>{l.phone ?? '—'}</div>
        <div className="truncate">{l.email ?? '—'}</div>
        <div className="truncate">{l.website ?? '—'}</div>
        <div className="truncate">{l.address}, {l.city}, {l.state}</div>
      </div>
    </div>
  );
}
